import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { AttemptText } from '../../zine';
import type { AnswerCorrectionLabels } from '../../zine';
import { zineColors, zineFonts } from '../../zine/zine-theme';
import type { ExerciseFeedbackMessage } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseFeedbackList } from '../shared/exercise-feedback-list';

/**
 * Splits exercise feedback into the message that becomes the workbook
 * Verdict box (auto-check result / submission status) and the richer
 * messages (AI grading with annotations, teacher comments) that keep
 * rendering through ExerciseFeedbackList so none of their logic is lost.
 */
export function splitWorkbookFeedback(feedback?: ExerciseFeedbackMessage[]): {
  verdictMessage: ExerciseFeedbackMessage | null;
  extraMessages: ExerciseFeedbackMessage[];
} {
  const verdictMessage =
    feedback?.find((message) => message.source === 'auto' || message.source === 'status') ?? null;
  const extraMessages = (feedback ?? []).filter((message) => message !== verdictMessage);
  return { verdictMessage, extraMessages };
}

interface WorkbookVerdictProps {
  message: ExerciseFeedbackMessage | null;
  /** replaces the message description (e.g. "2 of 3 blanks correct") */
  overrideText?: string;
  /**
   * The answer is being processed by the backend: the last result is replaced
   * by the "Awaiting correction…" card until the new result arrives.
   */
  pending?: boolean;
}

/**
 * The auto-check result / submission status under the workbook footer.
 * Rendered with the same `ExerciseFeedbackList` cards (same colours and
 * layout) as the legacy class page, so students see one feedback format
 * whichever design they are on.
 */
export function WorkbookVerdict({ message, overrideText, pending = false }: WorkbookVerdictProps) {
  if (pending) {
    return (
      <div className="mt-4 w-full">
        <ExerciseFeedbackList feedback={[]} pending />
      </div>
    );
  }

  if (!message) {
    return null;
  }

  const shown: ExerciseFeedbackMessage =
    overrideText != null ? { ...message, description: overrideText } : message;

  return (
    <div className="mt-4 w-full">
      <ExerciseFeedbackList feedback={[shown]} />
    </div>
  );
}

/**
 * Muted attempt counter for the workbook footer. Shows "First attempt"
 * before any submission exists; once a submission is recorded the attempt
 * number is displayed by the page next to the exercise, so this renders
 * "Attempt {{n}}" only when a number is reachable on the content.
 */
export function WorkbookAttemptText({ content }: { content: StudentExerciseContent }) {
  const { t } = useTranslation('p2e');

  if (!content.lastSubmission) {
    return <AttemptText>{t('zine.workbook.firstAttempt')}</AttemptText>;
  }

  const attemptNumber = (content as StudentExerciseContent & { attemptNumber?: number })
    .attemptNumber;
  if (typeof attemptNumber === 'number' && attemptNumber > 0) {
    return <AttemptText>{t('zine.workbook.attempt', { n: attemptNumber + 1 })}</AttemptText>;
  }

  return null;
}

/**
 * Rich AI/teacher feedback rendered below the verdict, unchanged in logic.
 * Hidden while `pending` (a new answer is being graded) so a stale correction
 * is never shown next to the "Awaiting correction…" box.
 */
export function WorkbookExtraFeedback({
  messages,
  pending = false,
}: {
  messages: ExerciseFeedbackMessage[];
  pending?: boolean;
}) {
  if (pending || !messages.length) {
    return null;
  }
  return (
    <div className="mt-4 w-full">
      <ExerciseFeedbackList feedback={messages} />
    </div>
  );
}

/** Shared inline style for white "clipped card" surfaces inside the panel. */
export const workbookCardStyle: CSSProperties = {
  background: '#fff',
  border: `1px solid ${zineColors.line}`,
  borderRadius: 4,
};

/** Shared inline style for condensed (Oswald) UI text inside the panel. */
export const workbookCondStyle: CSSProperties = {
  fontFamily: zineFonts.cond,
};

/** Translated labels for the shared inline-answer correction (mark + card). */
export function useAnswerCorrectionLabels(): AnswerCorrectionLabels {
  const { t } = useTranslation('p2e');
  return {
    seeFix: t('zine.workbook.correction.seeFix'),
    showAnswer: t('zine.workbook.correction.showAnswer'),
    correctMark: t('zine.workbook.correction.correctMark'),
    rightTitle: t('zine.workbook.correction.rightTitle'),
    wrongTitle: t('zine.workbook.correction.wrongTitle'),
    emptyTitle: t('zine.workbook.correction.emptyTitle'),
    yourAnswer: t('zine.workbook.correction.yourAnswer'),
    correct: t('zine.workbook.correction.correct'),
    close: t('zine.workbook.correction.close'),
  };
}
